import * as pc from "playcanvas";
import { resumeAudioContext } from "./audioContext";

/**
 * AudioMuffle — a master "concussion" bus that can muffle every game sound
 * at once, CS:GO-style, when an HE grenade goes off near the local player.
 *
 * How the global muffle works
 * ───────────────────────────
 * PlayCanvas has no master mix node — each SoundInstance wires its own
 * gain straight to `context.destination`. So to filter the WHOLE mix we
 * splice a bus in front of the speakers:
 *
 *     instance.gain ─┐
 *     instance.gain ─┼─▶ masterIn ─▶ lowpass ─▶ masterOut ─▶ destination
 *     instance.gain ─┘
 *
 * We patch `SoundInstance(3d).prototype._initializeNodes` once so every
 * instance created afterwards routes into `masterIn` instead of the
 * speakers. This mirrors the engine's own `setExternalNodes()` splice, so
 * it's a sanctioned graph edit — and it's transparent at rest (low-pass
 * wide open, master gain 1). Only `daze()` sweeps it.
 *
 * The daze itself = low-pass sweep (muffle) + a slight master duck + a
 * fading sine "tinnitus" ring (the ear-ringing), all scaled by how hard
 * the blast hit and auto-recovering over a couple of seconds.
 */

type SoundCtxApp = { soundManager?: { context?: AudioContext } };

/** Minimal view of the engine's private SoundInstance internals we touch. */
interface PatchInstance {
  _connectorNode?: AudioNode;
  _manager?: { context?: AudioContext };
}
interface PatchProto {
  _initializeNodes?: (this: PatchInstance) => void;
}

// Module-level so the one-time prototype patch can reach the live bus
// input. Set when the first AudioMuffle is constructed.
let busInput: AudioNode | null = null;
let patched = false;

/** Reroute every SoundInstance's output through `busInput` (once). */
function installRoutingPatch(): void {
  if (patched) return;
  const ctors = [pc.SoundInstance, pc.SoundInstance3d];
  for (const C of ctors) {
    const proto = (C as unknown as { prototype: PatchProto } | undefined)?.prototype;
    const orig = proto?._initializeNodes;
    if (!proto || typeof orig !== "function") continue;
    proto._initializeNodes = function (this: PatchInstance) {
      orig.call(this);
      // Move this instance's output from the speakers onto the muffle bus.
      // No-op (and harmless) if no bus exists yet.
      try {
        const dest = this._manager?.context?.destination;
        if (busInput && this._connectorNode && dest) {
          this._connectorNode.disconnect(dest);
          this._connectorNode.connect(busInput);
        }
      } catch {
        /* leave the default speaker connection intact on any mismatch */
      }
    };
  }
  patched = true;
}

const OPEN_HZ = 22000; // low-pass fully open = transparent
const DUCK_FLOOR = 0.5; // deepest master duck at full daze
const RING_HZ = 4300; // tinnitus tone base frequency
const RING_PEAK = 0.07; // max ring gain at full daze (kept subtle)

export class AudioMuffle {
  private readonly app: pc.AppBase;
  private readonly ctx: AudioContext | null;
  private readonly lowpass: BiquadFilterNode | null = null;
  private readonly masterOut: GainNode | null = null;
  private activeRing: { osc: OscillatorNode; gain: GainNode } | null = null;

  constructor(app: pc.AppBase) {
    this.app = app;
    // Reading `.context` lazily creates the AudioContext if needed.
    const ctx = (app as unknown as SoundCtxApp).soundManager?.context ?? null;
    this.ctx = ctx;
    if (!ctx) return; // no Web Audio — daze() is a no-op

    const masterIn = ctx.createGain();
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = OPEN_HZ;
    lowpass.Q.value = 0.7;
    const masterOut = ctx.createGain();
    masterOut.gain.value = 1;

    masterIn.connect(lowpass);
    lowpass.connect(masterOut);
    masterOut.connect(ctx.destination);

    this.lowpass = lowpass;
    this.masterOut = masterOut;

    busInput = masterIn;
    installRoutingPatch();
  }

  /**
   * Apply a concussion daze. `intensity` is 0..1 (proximity / damage of the
   * HE blast). Muffles all audio, ducks the master a touch and rings the
   * ears, then recovers on its own. Stacks gracefully — a fresh blast
   * deepens an ongoing muffle, never instantly clears it.
   */
  daze(intensity: number): void {
    const ctx = this.ctx;
    const lp = this.lowpass?.frequency;
    const mg = this.masterOut?.gain;
    if (!ctx || !lp || !mg) return;
    const k = Math.max(0, Math.min(1, intensity));
    if (k <= 0) return;

    resumeAudioContext(this.app);
    const now = ctx.currentTime;
    const onset = 0.06;

    // Stronger blast → darker cutoff, deeper duck, longer recovery.
    const cutoff = 700 - k * 440; // 700 Hz (light) → 260 Hz (heavy)
    const duck = 1 - k * (1 - DUCK_FLOOR); // 1.0 → 0.5
    const recover = 1.2 + k * 2.8; // 1.2 s → 4.0 s

    // Low-pass: dip to the darker of {current, new}, then open back up.
    const dip = Math.min(lp.value, cutoff);
    lp.cancelScheduledValues(now);
    lp.setValueAtTime(lp.value, now);
    lp.linearRampToValueAtTime(dip, now + onset);
    lp.exponentialRampToValueAtTime(OPEN_HZ, now + recover);

    // Master duck: dip to the lower of {current, new}, then restore.
    const duckTo = Math.min(mg.value, duck);
    mg.cancelScheduledValues(now);
    mg.setValueAtTime(mg.value, now);
    mg.linearRampToValueAtTime(duckTo, now + onset);
    mg.linearRampToValueAtTime(1, now + recover);

    this.ring(ctx, k, recover);
  }

  /** Fading sine ear-ring, wired straight to the speakers (NOT through the
   *  muffle bus — it's "in your head", so it isn't itself low-passed). */
  private ring(ctx: AudioContext, k: number, recover: number): void {
    // Retire any ring still sounding so they don't pile up.
    if (this.activeRing) {
      try {
        const t = ctx.currentTime;
        const g = this.activeRing.gain.gain;
        g.cancelScheduledValues(t);
        g.setValueAtTime(g.value, t);
        g.linearRampToValueAtTime(0.0001, t + 0.08);
        this.activeRing.osc.stop(t + 0.1);
      } catch {
        /* already stopped */
      }
      this.activeRing = null;
    }

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = RING_HZ + k * 700;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(RING_PEAK * k, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + recover);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + recover + 0.05);

    const entry = { osc, gain };
    osc.onended = () => {
      try {
        gain.disconnect();
        osc.disconnect();
      } catch {
        /* */
      }
      if (this.activeRing === entry) this.activeRing = null;
    };
    this.activeRing = entry;
  }
}
